import React, {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { useLocation, useMatch, useNavigate } from 'react-router-dom';
import { ONLINE, SUBCRIBE_TO_GAME_UPDATES } from 'constants/eventConstants';
import { DualSocketContext as SocketContext } from 'contexts/DualSocketContext';
import SplashScreenContext from 'contexts/SplashScreenContext';
import { useAppSelector } from 'hooks';
import { useIsMobile } from 'hooks/useIsMobile';
import { deriveActiveView } from 'utils/activeView';
import { consumePostLoginLanding } from 'utils/auth';
import { getDeviceId } from 'utils/functions';
import {
  LAST_OPEN_TABLES_KEY,
  readLastOpenTables,
  RESTORE_MAX_WAIT_MS
} from 'utils/lastOpenTables';
import { clearPlayerExitedTable, isExitedTable } from 'utils/recentlyExitedTables';
import {
  countHeldTableSlots,
  countUsedTableSlots,
  holdsTableSlot,
  MAX_OPEN_TABLES,
  queuedCardsOf,
  queuedGameIdsOf,
  readShownTableSlots,
  shownTableSlotsOf
} from 'utils/tableCapacity';
import { clampTablesInView, tablesThatFit } from 'utils/tableViewWidth';

import MaximumTablesReachedAlert from 'components/Waitlist/MaximumTablesReachedAlert';

// Re-exported so legacy importers (`import { MAX_OPEN_TABLES } from 'contexts/ViewManagerContext'`)
// keep working; the canonical definition lives in utils/tableCapacity.
export { MAX_OPEN_TABLES };

// Persisted "multi-table (All Tables grid) view" preference. Survives lobby
// round-trips and page refreshes so returning to a table keeps the grid the
// player chose, until they explicitly switch back to single-table view.
const MULTI_TABLE_PREF_KEY = 'multiTableViewEnabled';

const readMultiTablePref = (): boolean => {
  try {
    return localStorage.getItem(MULTI_TABLE_PREF_KEY) === 'true';
  } catch {
    return false;
  }
};

/**
 * How many tables the player CHOSE to have in view (see TableCountControl).
 *
 * Stored as the raw choice, never the clamped result. Pick 4 on a wide monitor
 * and reopen on a laptop and only two fit, so two is what renders — but the 4
 * is still what is remembered, and widening the window restores it. Storing the
 * clamped value instead would silently demote the preference the first time the
 * player used a smaller screen, and they would have to set it again every time.
 */
/**
 * The refresh-restore watcher's cadence and ceiling.
 *
 * A refresh restores the view you were in, and the decision is EVIDENCE-DRIVEN,
 * never a stopwatch: state.tables is empty at mount and fills in over
 * connect -> auth -> GOT_PLAYER_GAMES -> subscribe -> first broadcast, a chain
 * whose length varies with the environment (a lower env comfortably exceeds
 * any single number picked here — the first fixed-window version of this
 * raced that chain and lost, bouncing every refresh to the lobby). So the
 * watcher polls, and it only concludes "your tables are gone" when the engine
 * has SAID so: a GOT_PLAYER_GAMES push has been processed and it lists
 * nothing. The ceiling exists solely for the engine-never-answers case, where
 * the lobby (which runs its own connection recovery) is the least-bad landing.
 */
const RESTORE_EVIDENCE_POLL_MS = 400;
// Shared with useActiveTables' restore window; re-exported for existing importers.
export { RESTORE_MAX_WAIT_MS };

/**
 * How long a pending direct-grid open may hold the grid's too-narrow handoff
 * (see pendingGridReturnRef / hasPendingGridOpen below).
 *
 * The pending is armed mid-session, on a socket that is already up: one
 * subscribe → first-broadcast round trip, which the ~1Hz broadcaster lands in
 * a beat or two — when it lands at all. It does not always:
 * subscribeToNewTable returns silently when the socket is mid-reconnect, and
 * an engine that never answers broadcasts nothing. A pending with no ceiling
 * then held the handoff for the SESSION — the player sat in one-cell grid
 * chrome that only toggling Multi-Table View off could unstick (dev review
 * #3760). So the hold is bounded the way the restore's wait is bounded
 * (RESTORE_MAX_WAIT_MS), just smaller: that ceiling covers a full cold-boot
 * chain, this one a single mid-session round trip — generous for a slow
 * subscribe, short enough that a dropped one frees the handoff in seconds.
 */
export const PENDING_GRID_OPEN_MAX_MS = 8000;

/**
 * How long the balancing handler (TournamentScreenModal) waits after "Being
 * moved to another table" before it joins the destination — the grid's rebind
 * is armed at the START of that wait.
 */
export const BALANCING_MOVE_DELAY_MS = 3000;

/**
 * Ceiling on an armed grid rebind whose destination never lands (dev review
 * #3821). useGridSlots keeps the destination's placeholder cell alive for as
 * long as the rebind is set, and only the destination's arrival clears it — so
 * a dropped subscribe, or an engine that closes the destination after the
 * handler's late-abort recheck has passed, left the placeholder (and the
 * two-cell layout it props up) for the rest of the session. The bound is the
 * handler's wait plus the same dropped-subscribe ceiling a direct grid open
 * gets: past it the rebind is retired, the placeholder drops, and the grid's
 * cell count agrees with state.tables again, so the narrow handoff (or the
 * empty-grid lobby fallback) gets to run. A destination that lands later
 * still appears — as a fresh cell rather than in place.
 */
export const GRID_REBIND_MAX_MS = BALANCING_MOVE_DELAY_MS + PENDING_GRID_OPEN_MAX_MS;

/**
 * The live open-table set, persisted as { gameID: gameType } so a refresh can
 * re-subscribe WITHOUT waiting for the engine.
 *
 * Why this exists — the answer to "why did refresh always land in the lobby":
 * table data is pull-based. Nothing broadcasts until SOMEONE emits
 * SubscribeToGameUpdates for that table, and the only emitters were user
 * actions (openTable) and App.tsx's auto-resub, which runs only after
 * GOT_PLAYER_GAMES arrives over a freshly-authenticated engine socket —
 * connect, createPlayer, GOT_LOGIN, lobby-room registration, broadcaster
 * tick. On a cold boot that is the slowest chain in the app, and the lobby is
 * the only view that renders without per-table broadcasts, so the original
 * design sent every refresh there. Holding the route while WAITING on that
 * chain (the first version of the restore) just traded a bounce for a long
 * splash. This record removes the dependency: the client re-subscribes its
 * own last-known tables immediately, broadcasts flow over the API Gateway
 * socket within a moment, and the engine-login chain merely reconciles later
 * (App.tsx's auto-resub and its unsubscribe diff behave exactly as before).
 *
 * Written from live state, so it self-prunes: a closed table leaves
 * state.tables (removeTable) and drops out of the record on the next write.
 * Cleared on logout in utils/auth.ts clearAuth.
 */
// LAST_OPEN_TABLES_KEY / readLastOpenTables live in utils/lastOpenTables —
// shared with useActiveTables, whose order tracker must tell "closed" apart
// from "not restored yet" during the same boot window.

const TABLES_IN_VIEW_PREF_KEY = 'tablesInView';

// How long a view switch blocks the next one (a double-tapped chip can't stack
// two switches).
export const TABLE_TRANSITION_MS = 350;

// Loop guard for the view switches the client makes on its own — the grid's
// one-table handoff and the return to the grid (EN-11092). A return is refused
// once MAX_AUTO_VIEW_SWITCHES automatic switches already fall inside the
// window, so the most a loop can do is one round trip and the handoff after
// it: /grid → /table → /grid → /table, and the player stays on the table.
//
// Deliberately not a strict two-switch cap. Starting on /grid, ending on the
// table takes an odd number of switches, so capping at two would either leave
// the player on a one-cell grid or refuse a return within 5 s of every handoff
// — and a rebalance's new table normally loads 3–11 s after the handoff, so
// that would undo EN-10857's return to the grid. Handoffs are never refused:
// a one-table grid is the state the guard exists to get the player out of.
// Any navigation the player makes clears the record.
export const AUTO_VIEW_SWITCH_WINDOW_MS = 5000;
export const MAX_AUTO_VIEW_SWITCHES = 2;

/** Whether another automatic view switch would exceed the loop guard. */
export const autoViewSwitchesExhausted = (switchedAt: number[], nowMs: number): boolean =>
  switchedAt.filter((at) => nowMs - at < AUTO_VIEW_SWITCH_WINDOW_MS).length >=
  MAX_AUTO_VIEW_SWITCHES;

/** The record with one more automatic switch at `nowMs`, older ones dropped. */
const withAutoViewSwitch = (switchedAt: number[], nowMs: number): number[] =>
  [...switchedAt, nowMs].filter((at) => nowMs - at < AUTO_VIEW_SWITCH_WINDOW_MS);

// The grid's focused cell — see the view-state contract at selectedGridGameID.
const SELECTED_GRID_TABLE_KEY = 'selectedGridTable';

const readTablesInViewPref = (): number | null => {
  try {
    const raw = localStorage.getItem(TABLES_IN_VIEW_PREF_KEY);
    if (!raw) return null;
    const n = Number(raw);
    return Number.isFinite(n) && n >= 1 ? Math.floor(n) : null;
  } catch {
    return null;
  }
};

const writeTablesInViewPref = (count: number): void => {
  try {
    localStorage.setItem(TABLES_IN_VIEW_PREF_KEY, String(count));
  } catch {
    /* private mode / quota — in-memory state still drives the UI this session. */
  }
};

const writeMultiTablePref = (enabled: boolean): void => {
  try {
    localStorage.setItem(MULTI_TABLE_PREF_KEY, enabled ? 'true' : 'false');
  } catch {
    /* private mode / quota — in-memory state still drives the UI this session. */
  }
};

export type ActiveView = 'lobby' | 'table' | 'grid';

export type TableInfo = {
  gameID: string;
  gameType: string;
  observe?: string;
};

export type OpenTableOptions = {
  forceSingle?: boolean;
  fromGesture?: boolean;
  /**
   * The seat a rebalance or re-entry is leaving (EN-10421). That one table
   * doesn't count toward the cap — without waiting for the lobby's tournament
   * details to show the new seat, which can lag the tournament status that
   * started the move. Only that table: observed tables of the same tournament
   * stay open after the move and keep counting until the confirmed seat
   * closes them.
   */
  leavingGameID?: string | number | null;
};

type ViewManagerContextType = {
  activeView: ActiveView;
  activeTableInfo: TableInfo | null;
  isTableMounted: boolean;
  tableKey: string | number;
  gridPage: number;
  /**
   * Returns whether the open was accepted — false if a guard (the transition
   * debounce, maintenance, or the table-cap check) rejected it and nothing
   * navigated or subscribed. Callers that manufacture their own bookkeeping
   * for an open (e.g. useActiveTables' recordTableOpen) must gate on this
   * rather than treating the call itself as completion (EN-10857).
   */
  openTable: (
    gameID: string,
    gameType: string,
    observe?: string,
    opts?: OpenTableOptions
  ) => boolean;
  returnToLobby: () => void;
  /**
   * The grid's one-table handoff: open `gameID` in single-table view because
   * the grid fell to one table. When the window fits two, the grid comes back
   * once two tables are loaded again, unless the player navigates elsewhere
   * first. Returns whether the open was accepted, like openTable.
   */
  handOffGridToTable: (gameID: string, gameType: string) => boolean;
  /**
   * MultiTableGrid reports the table count its too-narrow handoff decides on
   * (the tables it would show, including a rebalance in flight). The return to
   * the grid and the pending grid open use this same count, so the two sides of
   * the handoff can never disagree about whether there is a grid to show
   * (EN-11092). Until the grid has reported, they fall back to state.tables.
   */
  reportGridTableCount: (count: number) => void;
  openGrid: () => void;
  setGridPage: (page: number) => void;
  /**
   * Persisted multi-table (All Tables grid) view preference. When true,
   * re-opening an already-open table lands in the grid instead of single-table
   * view, so the player's chosen layout survives lobby round-trips.
   */
  multiTableEnabled: boolean;
  setMultiTableEnabled: (enabled: boolean) => void;
  /**
   * How many tables the player asked to see at once, or null for "as many as
   * fit". Consumers must clamp it with clampTablesInView before laying
   * anything out — this is the stored CHOICE, not the achievable count.
   */
  tablesInView: number | null;
  /**
   * True while the refresh-restore watcher is still re-hydrating the view —
   * from arm until it settles (tables live, evidence says leave, or ceiling).
   * Consumers that react to a SMALL table set must hold off while this is
   * true: tables come back ONE BROADCAST AT A TIME, so mid-restore the set
   * transiently looks like "just one table open", which is exactly the state
   * the grid's too-narrow handoff reads as "grid is pointless, go to single
   * view" — yanking the player out of the grid before tables 2..n land
   * (owner repro: refresh at 3-up landed in single view with all three
   * tables in the dock).
   */
  restoreInFlight: boolean;
  setTablesInView: (count: number) => void;
  /**
   * Index into the ordered open tables of the LEFTMOST table currently on
   * screen. Only meaningful when more than one table shares the viewport; at
   * one-in-view it is simply the table you are on.
   *
   * App state rather than carousel-local state because the gesture layer needs
   * it too: whether a swipe can page further depends on where the window sits,
   * not on which table happens to be focused inside it.
   */
  tableWindowStart: number;
  setTableWindowStart: (start: number) => void;
  /**
   * The grid cell the player has selected as their focus in multi-table view.
   * Highlighted in both the grid and the top nav tabs. null = none selected
   * yet (MultiTableGrid defaults it to the first table).
   */
  selectedGridGameID: string | null;
  selectGridTable: (gameID: string) => void;
  /**
   * A direct-grid open is mid-flight: openTable subscribed a NEW table and
   * navigated to /grid, and that table's first broadcast has not landed yet.
   * While true, the grid's one-cell reading is a lie exactly the way a
   * restore's is — MultiTableGrid must hold its too-narrow handoff or it
   * hands the view to the OLD table and the pending focus can never land
   * (EN-10588). Callback (not state): read at effect time, never re-renders.
   * BOUNDED: a pending older than PENDING_GRID_OPEN_MAX_MS is retired on
   * read, so a dropped subscribe can never hold the handoff for the session
   * (dev review #3760) — but because expiry re-renders nothing, a consumer
   * holding on this must schedule its own re-check (see MultiTableGrid's
   * holdForPendingGridOpen).
   */
  hasPendingGridOpen: () => boolean;
  /**
   * A pending in-place grid cell rebind (an MTT balancing move): the cell
   * currently showing `from` should become `to` once `to` lands in
   * state.tables, keeping the cell mounted (no remove+add). Set by the balancing
   * handler; consumed + cleared by MultiTableGrid's slot layer.
   */
  gridRebind: { from: string; to: string } | null;
  rebindGridTable: (from: string, to: string) => void;
  clearGridRebind: () => void;
  /**
   * Gate for actions that will consume a future table slot (e.g. SnG
   * registration). Returns true when capacity is available; returns false
   * and pops the "Reached Table Limit" modal otherwise. Callers must not
   * proceed with the slot-consuming action when this returns false.
   */
  requestTableSlot: () => boolean;
  /**
   * True for exactly one mount: the hard reload a guest/member lands on right
   * after logging in from a non-lobby page (utils/auth's consumeLoginReturnPath).
   * Consumed ONCE here — see the comment above the useState call below for why
   * every other consumer (MultiTableGrid's post-login grace window) must read
   * it from context instead of calling consumePostLoginLanding() itself.
   */
  wasPostLoginLanding: boolean;
};

export const ViewManagerContext = createContext<ViewManagerContextType>({
  activeView: 'lobby',
  activeTableInfo: null,
  isTableMounted: false,
  tableKey: '0',
  gridPage: 0,
  openTable: () => false,
  handOffGridToTable: () => false,
  reportGridTableCount: () => {},
  returnToLobby: () => {},
  openGrid: () => {},
  setGridPage: () => {},
  multiTableEnabled: false,
  setMultiTableEnabled: () => {},
  tablesInView: null,
  restoreInFlight: false,
  setTablesInView: () => {},
  tableWindowStart: 0,
  setTableWindowStart: () => {},
  selectedGridGameID: null,
  selectGridTable: () => {},
  hasPendingGridOpen: () => false,
  gridRebind: null,
  rebindGridTable: () => {},
  clearGridRebind: () => {},
  requestTableSlot: () => true,
  wasPostLoginLanding: false
});

export const ViewManagerProvider = ({ children }: PropsWithChildren) => {
  const location = useLocation();
  const navigate = useNavigate();
  const tableMatch = useMatch('/table/:gameID/:gameType');
  const tables = useAppSelector((state) => (state as any).tables) as Record<string, any>;
  // Evidence that the engine has answered at all: GOT_PLAYER_GAMES replaces
  // state.playerGames with a NEW array on every push (even an empty one), so a
  // reference change from the mount-time value means "a push has been
  // processed" — which is exactly the difference between "you have no tables"
  // (safe to leave) and "the answer hasn't arrived yet" (keep waiting).
  const playerGames = useAppSelector((state) => (state as any).playerGames) as any[];
  const playerGamesRef = useRef(playerGames);
  playerGamesRef.current = playerGames;
  const initialPlayerGamesRef = useRef(playerGames);
  const playerGamesArrivedRef = useRef(false);
  if (playerGames !== initialPlayerGamesRef.current) playerGamesArrivedRef.current = true;
  const sitAndGoList = useAppSelector((state) => (state as any).lobby?.tournament?.sitAndGoList) as
    | any[]
    | undefined;
  const playerTournamentDetails = useAppSelector(
    (state) => (state as any).lobby?.playerTournamentDetails
  ) as any[] | undefined;
  // The player's waitlist spots — each holds a slot (see readHeldSlots).
  const waitlistSpots = useAppSelector((state) => (state as any).waitlist?.mySpotByGameId) as
    | Record<string, { gameId?: number | string; gameType?: string }>
    | undefined;
  const isMaintenance = useAppSelector(
    (state) => (state as any).maintenanceSchedule?.isMaintenance
  ) as boolean | undefined;

  // Socket + player identity — needed to subscribe a brand-new table directly
  // (see subscribeToNewTable) so multi-table opens can go straight to the grid
  // instead of detouring through single-table view to subscribe.
  const { socket, setGameType } = useContext(SocketContext);

  const playerGUID = useAppSelector((state) => (state as any).user?.playerGUID);
  const playerName = useAppSelector((state) => (state as any).user?.playerName);
  const playerLevel = useAppSelector((state) => (state as any).user?.playerLevel);
  const playerDisplayName = useAppSelector((state) => (state as any).user?.displayName);

  // Derive activeView from URL via the shared helper (utils/activeView), so
  // ViewManagerContext (under its own provider) and App.tsx (which sits above
  // it and can't consume this context) stay in lockstep. Prefix-matches
  // '/grid' — a trailing slash or future `/grid/*` sub-route still counts as
  // grid instead of silently collapsing to 'lobby'.
  const activeView: ActiveView = useMemo(
    () => deriveActiveView(location.pathname, tableMatch),
    [tableMatch, location.pathname]
  );

  // TURN THE SOCKETS ON FOR THE GRID. ConnectionManager.initialize() runs only
  // when a page calls setGameType (a one-shot latch — later calls no-op), and
  // exactly three pages do: Lobby, GuestLobby, and Game. A cold boot on /grid
  // mounts NONE of them (the grid has no cells until state.tables hydrates,
  // and state.tables cannot hydrate without sockets), so the entire stack —
  // engine login, GOT_PLAYER_GAMES, subscriptions, broadcasts — simply never
  // started. That circle is the real reason "refresh always lands in the
  // lobby" existed: the lobby was the only landing guaranteed to flip this
  // switch. Confirmed from a live console capture (owner, 8/24): stuck on
  // /grid there is no "[ConnectionManager] Initializing" line at all; it
  // appears the instant the lobby mounts. The grid announces itself the same
  // way the lobby does; in-session entries are no-ops via the latch.
  useEffect(() => {
    if (activeView === 'grid') setGameType('lobby');
  }, [activeView, setGameType]);

  // Derive activeTableInfo from URL params
  const activeTableInfo: TableInfo | null = useMemo(() => {
    if (!tableMatch) return null;
    const searchParams = new URLSearchParams(location.search);
    return {
      gameID: tableMatch.params.gameID || '',
      gameType: tableMatch.params.gameType || '',
      observe: searchParams.get('observe') || 'no'
    };
  }, [tableMatch, location.search]);

  // Consumed exactly ONCE, synchronously, during this component's own render
  // — i.e. before any descendant (including the always-mounted MultiTableGrid,
  // rendered as SlideContainer's gridPanel regardless of activeView) gets to
  // render at all. React guarantees a parent's function body runs before its
  // children's, so capturing the one-shot flag here — rather than in each
  // consumer's own useEffect/useState — is what makes it possible to hand the
  // SAME value to every consumer via context instead of racing them against
  // each other for a flag that clears itself on first read.
  //
  // Regression this fixes: MultiTableGrid used to call consumePostLoginLanding()
  // itself in a useState initializer, which runs during render — strictly
  // BEFORE the useEffect below (effects always run after the full commit).
  // Since MultiTableGrid mounts unconditionally on every route, it silently won
  // that race every time, clearing the flag before this effect ever saw it —
  // so a guest returning to a TABLE page after login (e.g. from the "Login to
  // contact support" advisory) got bounced straight back to the lobby by the
  // refresh-guard below, which always found the flag already gone.
  const [wasPostLoginLanding] = useState(consumePostLoginLanding);
  // Splash control for the refresh-restore watcher; the provider defaults are
  // no-ops, so tests without a SplashScreenProvider stay unaffected.
  const splash = useContext(SplashScreenContext);
  // See ViewManagerContextType.restoreInFlight.
  const [restoreInFlight, setRestoreInFlight] = useState(false);

  // A REFRESH RESTORES THE VIEW YOU WERE IN (owner call, 2026-08-24 — this
  // replaced the original "any table URL on mount redirects to the lobby"
  // Unity-parity rule). At a table you stay at that table; in the grid you
  // stay in the grid with the count clamped to what actually came back.
  //
  // Live reads for the watcher below: it is armed once, on mount, long before
  // any of this state has anything useful in it.
  const tablesRef = useRef(tables);
  tablesRef.current = tables;
  const openTableRef = useRef<ViewManagerContextType['openTable'] | null>(null);
  const subscribeToNewTableRef = useRef<((gameID: string, gameType: string) => void) | null>(null);
  const splashRef = useRef(splash);
  splashRef.current = splash;
  const socketRef = useRef(socket);
  socketRef.current = socket;

  // Keep the persisted open-set record current (see LAST_OPEN_TABLES_KEY).
  // Guarded by a serialized snapshot so the ~1/s broadcast ticks don't write
  // localStorage unless the SET or a game type actually changed.
  const lastOpenTablesWrittenRef = useRef('');
  useEffect(() => {
    // EVERY live table must land in the record, whatever its payload has so
    // far. Requiring t.game.game meant a table whose broadcast had not yet
    // filled that field (it initialises to '') was silently absent — and the
    // consumers treat "absent" as "closed", which is how a still-arriving
    // table lost its slot and re-entered as newest, swapping the dock order.
    // The type falls back to what we already knew, then to the engine's own
    // default, so an entry is never dropped for want of a label.
    const previous = readLastOpenTables();
    // While the restore is still running the live set is by definition
    // PARTIAL, so replacing the record with it would throw away exactly the
    // tables still on their way — and the consumers read "absent" as "closed".
    // Merge instead; the window closes once everything seeded has landed, and
    // a table genuinely closed while away is dropped by the first full write
    // after that.
    const record: Record<string, string> = restoreInFlight ? { ...previous } : {};
    Object.entries(tables || {}).forEach(([gid, t]: [string, any]) => {
      if (t?.game?.gameID && t.game.gameID !== 0) {
        record[gid] = t.game.game || previous[gid] || 'texas';
      }
    });
    const serialized = JSON.stringify(record, Object.keys(record).sort());
    if (serialized === lastOpenTablesWrittenRef.current) return;
    lastOpenTablesWrittenRef.current = serialized;
    // An EMPTY set is deliberately not persisted over a non-empty one from
    // this effect: state.tables is transiently empty during boot and during
    // socket recovery, and overwriting the record then would erase exactly
    // the information the next refresh needs. The record self-corrects on the
    // next non-empty write, and a genuinely table-free session ends via
    // logout (clearAuth removes the key) or the next session's reconcile.
    if (Object.keys(record).length === 0) return;
    try {
      localStorage.setItem(LAST_OPEN_TABLES_KEY, serialized);
    } catch {
      /* private mode / quota — the slow reconcile path still works. */
    }
  }, [tables, restoreInFlight]);

  useEffect(() => {
    const initialPath = window.location.pathname;
    const atTable = !!tableMatch;
    const atGrid = deriveActiveView(initialPath, tableMatch) === 'grid';
    const atLobby = initialPath === '/';
    if (!atTable && !atGrid && !atLobby) return undefined;
    // The post-login landing is its own, already-correct restore (a hard
    // reload straight back to the table URL) — don't run a second one under it.
    if (wasPostLoginLanding) return undefined;
    // Guests keep the legacy redirect: they cannot be seated, so there is
    // nothing the engine could restore — waiting would just strand them.
    if (!localStorage.getItem('playerGUID')) {
      if (!atLobby) navigate('/', { replace: true });
      return undefined;
    }
    const urlGameID = atTable ? String((tableMatch as any).params?.gameID ?? '') : '';
    const urlGameType = atTable ? String((tableMatch as any).params?.gameType ?? '') : '';

    // Cover the wait with the branded splash — GRID ONLY. The table route
    // covers its own boot (Game plays its 'gameIntro' entrance once the felt
    // hydrates), so a watcher splash there stacked two branded splashes
    // back-to-back on every table refresh (owner repro, 8/25: quick splash,
    // second splash, then the fade). The grid has no intro of its own.
    if (atGrid) splashRef.current.showSplash('refreshRestore', 'full');
    setRestoreInFlight(true);

    // RE-SUBSCRIBE FIRST, THEN WAIT. The client already knows what was open
    // (LAST_OPEN_TABLES_KEY) — emitting the subscribes itself makes broadcasts
    // flow over the API Gateway socket in a moment, instead of waiting out the
    // engine-login chain that produces GOT_PLAYER_GAMES (see the key's
    // docblock: that chain is WHY refresh historically dumped players in the
    // lobby). Subscribing is idempotent server-side (a TTL'd upsert), a closed
    // table's subscribe is inert (nothing broadcasts), and App.tsx's
    // auto-resub reconciles the record against playerGames afterwards exactly
    // as it always has. Seeded on the first tick with a socket, and once more
    // a few ticks later in case the first emits raced the socket's own
    // connect/auth — two rounds at most.
    const seedTargets = (): Record<string, string> => {
      const targets = readLastOpenTables();
      if (atTable && urlGameID && !targets[urlGameID]) {
        targets[urlGameID] = urlGameType || 'texas';
      }
      return targets;
    };
    const targets = seedTargets();
    const seededSomething = Object.keys(targets).length > 0;
    // What "the grid is back" means. One live cell is NOT it: with exactly one
    // table live, tablesAcross clamps to 1 and the too-narrow handoff (held
    // only while restoreInFlight) would fire in the gap before cell #2
    // hydrates — a 2-table grid refresh landed in single-table view (owner
    // repro, 8/25). Two live cells make the handoff impossible
    // (tablesAcross >= 2), so settle there; a genuinely-single remembered
    // table settles at one and hands off as designed. If one of two remembered
    // tables closed while away, the ceiling settles it — slower, but it lands
    // exactly where a one-table grid should.
    const gridSettleCount = Math.min(2, Math.max(1, Object.keys(targets).length));
    let seedRounds = seededSomething ? 0 : 2;
    let ticksSinceSeed = 0;
    const trySeed = (): void => {
      if (seedRounds >= 2 || !socketRef.current || !subscribeToNewTableRef.current) return;
      ticksSinceSeed += 1;
      // Round 1 immediately; round 2 only if ~3s later nothing is live yet.
      if (seedRounds === 1 && ticksSinceSeed < 8) return;
      Object.entries(targets).forEach(([gid, game]) => {
        subscribeToNewTableRef.current?.(gid, game);
      });
      seedRounds += 1;
      ticksSinceSeed = 0;
    };

    const isLive = (gid: string): boolean => {
      const t = tablesRef.current?.[gid];
      return !!t?.game?.gameID && t.game.gameID !== 0;
    };
    // TWO SETTLES, not one.
    //
    // NAVIGATION settles as soon as the view is back — for the grid that is
    // two live cells, the point where the too-narrow handoff can no longer
    // fire. Holding the splash past that is dead time on screen.
    //
    // HYDRATION runs longer: until every table we seeded is live, or the
    // ceiling. It must, because restoreInFlight is what stops useActiveTables
    // pruning a partially-arrived set, and a partial prune turns the
    // stragglers' slots into vacancies they may not land back in. Seated
    // tables return first (the engine re-subscribes them off its
    // own player-games push) while observed tables come only from our boot
    // seed, so ending this window at the navigation settle re-grouped the dock
    // BY STATUS: the observed pair jumped ahead of the seated pair (owner
    // repro, 8/25). The record write above holds for the same reason.
    let navDone = false;
    let hydrationDone = false;
    let iv = 0 as unknown as ReturnType<typeof setInterval>;
    let cap = 0 as unknown as ReturnType<typeof setTimeout>;
    // The timers drive BOTH settles, so they stop only once both are done —
    // with nothing seeded, hydration is finished immediately and the polling
    // still has to run for the navigation evidence.
    const maybeStop = (): void => {
      if (!navDone || !hydrationDone) return;
      clearInterval(iv);
      clearTimeout(cap);
    };
    const endHydration = (): void => {
      if (hydrationDone) return;
      hydrationDone = true;
      setRestoreInFlight(false);
      maybeStop();
    };
    const finish = (fn?: () => void): void => {
      if (navDone) return;
      navDone = true;
      splashRef.current.hideSplash('refreshRestore');
      fn?.();
      maybeStop();
    };
    const allSeededLive = (): boolean => Object.keys(targets).every(isLive);

    const decide = (final: boolean): void => {
      // Restore the lobby's previews in the background without leaving it.
      if (atLobby) return finish();
      // The player (or our own fallback) navigated — whatever they chose wins,
      // and the restore is moot.
      if (window.location.pathname !== initialPath) {
        finish();
        endHydration();
        return;
      }

      const t = tablesRef.current || {};
      const liveIds = Object.keys(t).filter(isLive);

      // The view restored itself: the URL's table is live again, or the grid
      // has cells to show. Done — nothing to decide.
      if (atTable && isLive(urlGameID)) return finish();
      if (atGrid && liveIds.length >= gridSettleCount) return finish();

      const arrived = playerGamesArrivedRef.current;
      const pg = (playerGamesRef.current || []) as any[];

      // We subscribed the remembered set (the URL's table always included), so
      // anything that still exists WILL hydrate — broadcasts arrive in random
      // order and playerGames lists seated tables only, so neither is grounds
      // to give up on a seeded table early. Doing so is what made the focused
      // table jump to whichever broadcast landed first (owner repro, 8/25).
      // Before the ceiling, the only decisive signal is the engine answering
      // when NOTHING was seeded: then nothing can ever hydrate, and an empty
      // answer means an empty session — leave for the lobby early.
      if (!final) {
        if (!seededSomething && arrived && pg.length === 0) {
          return finish(() => navigate('/', { replace: true }));
        }
        return; // hold — seeded tables still hydrating, or no evidence yet
      }

      // The ceiling: the URL's table never came back (closed while away, or
      // an engine that never answered). Land on a table that DID come back
      // rather than the lobby when there is one.
      if (atTable && liveIds[0] && openTableRef.current) {
        const fallback = liveIds[0];
        const game = t[fallback]?.game?.game || 'texas';
        return finish(() => openTableRef.current?.(fallback, game, 'no', { forceSingle: true }));
      }
      return finish(() => navigate('/', { replace: true }));
    };

    const tick = (final: boolean): void => {
      trySeed();
      if (!navDone) decide(final);
      // The window closes when everything we asked for has landed — or at the
      // ceiling, for tables that are simply gone.
      if (final || allSeededLive()) endHydration();
    };
    iv = setInterval(() => tick(false), RESTORE_EVIDENCE_POLL_MS);
    cap = setTimeout(() => tick(true), RESTORE_MAX_WAIT_MS);
    tick(false);
    return () => {
      clearInterval(iv);
      clearTimeout(cap);
      finish();
      endHydration();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // isTableMounted is now derived — true only when on a table route
  const isTableMounted = activeView === 'table';

  // tableKey — use the gameID itself as the key so React unmounts/remounts
  // the Game component whenever the table changes. No counter needed.
  const tableKey = activeTableInfo?.gameID || '0';

  const isMobile = useIsMobile();

  // Grid page — local UI state, not URL-worthy
  const [gridPage, setGridPage] = useState(0);

  // Persisted multi-table view preference (see MULTI_TABLE_PREF_KEY). Seeded
  // from localStorage so it survives refreshes and lobby round-trips.
  const [multiTableEnabled, setMultiTableEnabledState] = useState<boolean>(readMultiTablePref);
  const setMultiTableEnabled = useCallback((enabled: boolean) => {
    setMultiTableEnabledState(enabled);
    writeMultiTablePref(enabled);
  }, []);

  // How many tables the player wants in view (see TABLES_IN_VIEW_PREF_KEY).
  // Remembered, not recomputed: every surface caps it at the tables open, so
  // the view returns to it whenever that many are open again.
  const [tablesInView, setTablesInViewState] = useState<number | null>(readTablesInViewPref);
  const tablesInViewRef = useRef(tablesInView);
  tablesInViewRef.current = tablesInView;
  // How many tables are open now (null before the first count), and the most
  // open since the count was last set (see the auto-grow effect below).
  const openTableCountRef = useRef<number | null>(null);
  const mostOpenSinceChoiceRef = useRef<number>(0);
  // The set a reload is restoring: tables coming back one broadcast at a time
  // are not new ones. It counts only until that many are open or the restore
  // window ends — a table that closed while the player was away never comes
  // back, and must not hold the mark up for the rest of the session.
  const restoreTargetRef = useRef({
    count: Object.keys(readLastOpenTables()).length,
    until: Date.now() + RESTORE_MAX_WAIT_MS
  });
  const setTablesInView = useCallback((count: number) => {
    setTablesInViewState(count);
    writeTablesInViewPref(count);
    mostOpenSinceChoiceRef.current = openTableCountRef.current ?? mostOpenSinceChoiceRef.current;
  }, []);

  // Not persisted: which slice of the open tables is on screen is a property of
  // this session's scroll position, not a preference.
  const [tableWindowStart, setTableWindowStart] = useState(0);

  // Which table the player has selected as their focus in multi-table view.
  //
  // PERSISTED — this is the grid half of "a refresh restores the view you
  // were in". The full view-state contract across a reload, for the record:
  //   • which tables are open .... localStorage.lastOpenTables (re-subscribed
  //                                at boot by the restore watcher)
  //   • their order .............. localStorage.subscribedTablesOrder (the
  //                                tracker; restore-aware pruning)
  //   • single vs grid ........... the URL (/table/:id vs /grid)
  //   • table count (2/3/4-up) ... localStorage.tablesInView
  //   • focused table, single .... the URL itself (held by the watcher)
  //   • focused table, grid ...... THIS key
  //   • seated vs observed ....... deliberately NOT persisted — the engine is
  //                                the authority and re-derives it from
  //                                playerGames + broadcasts on every boot.
  const [selectedGridGameID, setSelectedGridGameIDState] = useState<string | null>(() => {
    try {
      return localStorage.getItem(SELECTED_GRID_TABLE_KEY) || null;
    } catch {
      return null;
    }
  });
  const setSelectedGridGameID = useCallback((gameID: string | null) => {
    setSelectedGridGameIDState(gameID);
    try {
      if (gameID) localStorage.setItem(SELECTED_GRID_TABLE_KEY, gameID);
      else localStorage.removeItem(SELECTED_GRID_TABLE_KEY);
    } catch {
      /* private mode / quota — in-memory still drives this session. */
    }
  }, []);
  const selectGridTable = useCallback(
    (gameID: string) => {
      setSelectedGridGameID(gameID ? String(gameID) : null);
    },
    [setSelectedGridGameID]
  );

  // Pending in-place grid cell rebind for an MTT balancing move (see the type
  // doc). MultiTableGrid's slot layer applies it once `to` lands, then clears it.
  const [gridRebind, setGridRebind] = useState<{ from: string; to: string } | null>(null);
  const rebindGridTable = useCallback((from: string, to: string) => {
    const next =
      from && to && String(from) !== String(to) ? { from: String(from), to: String(to) } : null;
    // Same pair → same object. The balancing handler re-arms on every
    // gotTnmStatus tick during its 3s wait (dev re-review #3821); a fresh
    // object each time would restart the bound below, so the hold is anchored
    // to the FIRST arming instead. A different destination is a new move and
    // does restart it.
    setGridRebind((prev) =>
      prev && next && prev.from === next.from && prev.to === next.to ? prev : next
    );
  }, []);
  const clearGridRebind = useCallback(() => setGridRebind(null), []);
  // BOUNDED (dev review #3821): retire a rebind whose destination has not
  // landed within GRID_REBIND_MAX_MS of its FIRST arming. A repeat of the same
  // pair is a no-op (above); a new destination restarts the clock; consumption
  // or an explicit clear cancels it. See the constant's note for what an
  // unbounded hold cost.
  useEffect(() => {
    if (!gridRebind) return undefined;
    const timer = setTimeout(() => setGridRebind(null), GRID_REBIND_MAX_MS);
    return () => clearTimeout(timer);
  }, [gridRebind]);

  // Modal state for the cap-blocked open attempt
  const [showTableLimitModal, setShowTableLimitModal] = useState(false);

  // Transition guard — prevents rapid double-taps
  const transitionRef = useRef(false);
  // The grid's own table count (see reportGridTableCount), null until reported.
  const [gridTableCount, setGridTableCount] = useState<number | null>(null);
  const reportGridTableCount = useCallback((count: number) => setGridTableCount(count), []);
  // When the client last switched /grid ↔ /table on its own (the loop guard).
  const autoViewSwitchesRef = useRef<number[]>([]);
  // A return to the grid armed by its one-table handoff (see handOffGridToTable).
  const returnToGridRef = useRef(false);
  const transitionTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // When multi-table view is on and the player opens a BRAND-NEW table, we must
  // briefly route through /table so GameInner mounts and subscribes it (grid
  // cells don't subscribe). This holds that table's gameID so the effect below
  // can send the player back to /grid — keeping multi-table view on — the moment
  // the table lands in state.tables, instead of stranding them in single view.
  //
  // BOUNDED AND SUPERSEDABLE (dev review #3760). armedAtMs caps the hold at
  // PENDING_GRID_OPEN_MAX_MS: a subscribe that was dropped (socket
  // mid-reconnect) or never answered would otherwise leave it armed for the
  // session, permanently disabling the grid's too-narrow handoff AND its
  // lobby fallback. And every navigation branch of openTable settles it
  // explicitly — the arming branches re-arm it fresh, the others clear it —
  // so a newer explicit open supersedes the hold and it can never outlive
  // the open it was armed for. (Blocked opens — the transition debounce,
  // maintenance, the table cap — settle nothing: they are refusals, not
  // newer opens.)
  const pendingGridReturnRef = useRef<{ gameID: string; armedAtMs: number } | null>(null);

  const startTransition = useCallback(() => {
    transitionRef.current = true;
    if (transitionTimer.current) clearTimeout(transitionTimer.current);
    transitionTimer.current = setTimeout(() => {
      transitionRef.current = false;
    }, TABLE_TRANSITION_MS);
  }, []);

  // No auto-cleanup on navigate. Open tables (seated and observed, cash and
  // tournament) persist in state.tables until the user explicitly closes via
  // ExitTableButton. The 4-table cap (MAX_OPEN_TABLES) bounds growth, and
  // the All Tables grid + header chips both read state.tables — keeping them
  // in sync avoids the chip-vs-grid mismatch that previously hid the grid's
  // bottom page-nav whenever an observed table got auto-removed.
  //
  // Earlier behaviour: EN-9594 exempted tournament observers; EN-9638
  // re-enabled cleanup for cash observers and broke that intent. See commit
  // history before re-introducing any auto-cleanup here.

  // Slot accounting — open tables + pending SnG registrations. Centralised
  // so the open-table path and the SnG-register path share the same cap.
  // Exited tables are left out, as the nav strip leaves them out (EN-11131).
  // Computed at call time, not memoized: the exit marks live in localStorage,
  // outside these dependencies, so a memo could keep counting a table the
  // player has since exited (or reopened).
  const readSlotUsage = useCallback(
    () => countUsedTableSlots(tables, sitAndGoList, playerTournamentDetails, isExitedTable),
    [tables, sitAndGoList, playerTournamentDetails]
  );

  // The slots the cap counts: what the Current Tables strip shows (its tables,
  // Sit & Go registrations and waitlist cards) plus every open table — the
  // same count the strip's "+" uses (countHeldTableSlots). Read at call time so
  // a join is checked against the strip the player is looking at, with the
  // exit marks as they are now (EN-11131). With no strip mounted, the queued
  // tables still hold their slots, read from the store. Returned with the
  // cards it counted, which say which game each slot is for (holdsTableSlot).
  const readHeldSlots = useCallback(
    (slotUsage = readSlotUsage()) => {
      const shown =
        readShownTableSlots() ??
        shownTableSlotsOf(
          queuedCardsOf(waitlistSpots, tables),
          queuedGameIdsOf(waitlistSpots, tables)
        );
      return { ...countHeldTableSlots(shown, slotUsage, isExitedTable), shown };
    },
    [readSlotUsage, waitlistSpots, tables]
  );

  // Capacity gate for actions that will consume a future table slot
  // (currently: SnG registration). Returns true when the action may
  // proceed; returns false and pops the cap modal otherwise. The
  // openTable path uses the same accounting inline because it has an
  // "already open is fine" exemption that doesn't apply to register.
  const requestTableSlot = useCallback(() => {
    const { held, total, unshownSngs } = readHeldSlots();
    if (total >= MAX_OPEN_TABLES) {
      console.log('[ViewManager] requestTableSlot blocked — cap reached:', {
        cap: MAX_OPEN_TABLES,
        heldIds: Array.from(held),
        unshownSngs
      });
      setShowTableLimitModal(true);
      return false;
    }
    return true;
  }, [readHeldSlots]);

  // Subscribe to a brand-new table WITHOUT routing through the single-table
  // view. Mirrors GameService.joinTable's two emits: ONLINE registers the player
  // at the table; SubscribeToGameUpdates opens the API Gateway fan-out that
  // delivers GOT_TEXAS/GOT_OMAHA into state.tables. This lets a multi-table open
  // land directly in the grid — the table shows up as a cell the moment its
  // first broadcast arrives, with no single-table flash. Seat-specific fields
  // use fresh-observer defaults (the /table path sends stale currentTable values
  // here too, which the engine ignores for a subscribe).
  const subscribeToNewTable = useCallback(
    (gameID: string, gameType: string) => {
      if (!socket) return;
      const game = gameType || 'texas';
      const guid = playerGUID || localStorage.getItem('playerGUID') || '';
      const name = playerName || localStorage.getItem('playerName') || '';
      const deviceId = getDeviceId();
      socket.emit(ONLINE, {
        gameID,
        tableID: gameID,
        game,
        username: name,
        socketId: socket.id,
        data: '',
        playerGUID: guid,
        level: playerLevel || 0,
        seatId: -1,
        clubId: '',
        language: 'en',
        displayName: playerDisplayName || '',
        tournamentId: 0,
        uid: '',
        deviceId
      });
      socket.emit(SUBCRIBE_TO_GAME_UPDATES, {
        gameID,
        game,
        playerGUID: guid,
        username: name,
        deviceId
      });
    },
    [socket, playerGUID, playerName, playerLevel, playerDisplayName]
  );

  // Navigation functions — these change the URL, which changes the derived state
  const openTable = useCallback(
    (gameID: string, gameType: string, observe = 'no', opts?: OpenTableOptions) => {
      // The 350ms transition debounce exists so a double-tapped nav chip can't
      // stack two switches. It must NOT swallow gesture-driven opens: a rapid
      // swipe sequence legitimately commits tables ~250ms apart, and this
      // guard silently dropping the second commit was the "swipe gets stuck
      // on the same table when swiping fast" bug (reproduced 5/5 on a chained
      // double; investigation doc 8r). The gesture layer has its own ordering
      // machinery (pending-commit window + chained glide), so it bypasses.
      if (transitionRef.current && !opts?.fromGesture) return false;

      const slotUsage = readSlotUsage();

      // During maintenance, block opening a NEW table (Unity parity: "cannot
      // click on any tables"). Returning to an already-open table is still
      // allowed so a seated player can get back to finish their in-progress
      // hand before the after-hand redirect to the lobby fires.
      if (isMaintenance && !slotUsage.isAlreadyOpen(gameID)) {
        console.log('[ViewManager] openTable blocked — maintenance in progress:', gameID);
        return false;
      }

      // MTT-transition cap exemption. When the target gid corresponds to a
      // fresh 'P' assignment (bust+re-entry landed on a new seat, or a
      // rebalance moved the player to a different table), any OTHER table
      // already in state.tables for that SAME tournament is a stale sibling
      // — the old seat before the transition. App.tsx's fast-feed cleanup
      // will drop the sibling on the next details push, but only once the
      // destination has HYDRATED (its destination-not-hydrated guard exists
      // so the grid doesn't briefly empty a tournament and bounce the
      // player to the lobby). The destination can't hydrate until openTable
      // subscribes it — so counting the sibling here creates a deadlock
      // that the player experiences as a "Reached Table Limit" modal. Since
      // the sibling is guaranteed to be removed the moment the destination
      // hydrates, exempting it from the cap is safe: at no point does the
      // player actually hold more than MAX_OPEN_TABLES tables that they'll
      // still have a tick later.
      //
      // Strict scope:
      //  • Target gid MUST correspond to a status:'P' record in
      //    playerTournamentDetails (an ACTIVE tournament seat).
      //  • Only exempts other tables with the SAME tournamentId — cash
      //    tables, other tournaments, and SnGs are untouched.
      //  • Re-opening a table already open (in state.tables and not exited —
      //    an exited table holds no slot) is exempt from the cap (isAlreadyOpen).
      const targetDetail = (playerTournamentDetails || []).find(
        (d: any) =>
          d?.status === 'P' &&
          d?.gameID !== null &&
          d?.gameID !== undefined &&
          String(d.gameID) === String(gameID)
      );
      const targetTournamentId =
        targetDetail?.tournamentId !== null && targetDetail?.tournamentId !== undefined
          ? Number(targetDetail.tournamentId)
          : null;
      const staleSiblingGids = new Set<string>();
      if (
        targetTournamentId !== null &&
        Number.isFinite(targetTournamentId) &&
        targetTournamentId !== 0
      ) {
        Object.entries(tables || {}).forEach(([openGid, t]: [string, any]) => {
          if (String(openGid) === String(gameID)) return;
          const otherTournamentId = t?.game?.tournamentId;
          if (otherTournamentId === undefined || otherTournamentId === null) return;
          if (Number(otherTournamentId) !== targetTournamentId) return;
          staleSiblingGids.add(String(openGid));
        });
      }
      if (opts?.leavingGameID) {
        // Before the lobby details catch up, the move names the seat it is
        // leaving: that table, if it is still open, and no other. Once they
        // have, it is exempt as well as the same-tournament siblings: a Sit &
        // Go rematch (EN-11109) leaves a table of the PREVIOUS tournament,
        // which the sibling rule never matches.
        const leaving = String(opts.leavingGameID);
        if (leaving !== String(gameID) && slotUsage.openIds.has(leaving)) {
          staleSiblingGids.add(leaving);
        }
      }
      // Counted with the exit marks as they are now: a rebalance marks the seat
      // it leaves exited in the same tick it opens the new one, before the
      // strip has re-rendered without it (EN-10421).
      const heldSlots = readHeldSlots(slotUsage);
      // Only a sibling that actually holds a slot is exempted: an exited one is
      // already left out of the count, and subtracting it again would let a
      // fifth table through.
      const heldStaleSiblings = Array.from(staleSiblingGids).filter((gid) =>
        heldSlots.held.has(gid)
      ).length;
      // A table that holds a slot it isn't subscribed to yet — a waitlist card,
      // a Sit & Go registration, a seat listed from playerGames — uses that
      // slot when it opens; any other table — including the other game's
      // table with the same gameID — takes a new one.
      const holdsOwnSlot =
        holdsTableSlot(heldSlots.shown, heldSlots.held, gameID, gameType) ||
        slotUsage.hasPendingSngSlot(gameID);
      const slotsAfterOpen = heldSlots.total - heldStaleSiblings + (holdsOwnSlot ? 0 : 1);

      // Cap check: a player may have at most MAX_OPEN_TABLES tables open at
      // once, counted as the Current Tables strip shows them plus any open
      // table it collapses (EN-11131), the count the server's subscriptions
      // settle to (an unsubscribe still in flight is EN-11223). Switching
      // back to a table already open (subscribed) takes no slot. Opening one
      // that only holds a slot is allowed while the count stays within the
      // cap — not when it is already over it (a waitlist joined from another
      // device), where the subscription would be the fifth. Pending SnG
      // registrations consume future slots and count toward the total so the
      // user gets blocked at the earliest reasonable point instead of when the
      // SnG eventually auto-opens.
      if (!slotUsage.isAlreadyOpen(gameID) && slotsAfterOpen > MAX_OPEN_TABLES) {
        console.log('[ViewManager] openTable blocked — cap reached:', {
          cap: MAX_OPEN_TABLES,
          heldIds: Array.from(heldSlots.held),
          openIds: Array.from(slotUsage.openIds),
          pendingSngCount: slotUsage.pendingSngCount,
          attempted: gameID,
          // TEMP DIAGNOSTIC (table-cap): the raw inputs, so a false block can be
          // traced to the exact table keys vs SnG tournament/gameID mismatch.
          tableKeys: Object.entries(tables || {}).map(([id, t]: any) => ({
            key: id,
            gameID: t?.game?.gameID,
            type: t?.game?.tabletype
          })),
          sng: (sitAndGoList || []).map((s: any) => ({
            tournamentId: s?.tournamentId,
            id: s?.id,
            gameID: s?.gameID
          })),
          regDetails: (playerTournamentDetails || [])
            .filter((d: any) => d?.status === 'R')
            .map((d: any) => ({
              tournamentId: d?.tournamentId,
              gameID: d?.gameID,
              status: d?.status
            }))
        });
        setShowTableLimitModal(true);
        return false;
      }

      console.log('[ViewManager] openTable:', gameID, gameType);

      // Clear any "recently exited" mark for this table — the user is
      // explicitly re-opening it, so the auto-resub should treat it normally.
      try {
        const stored = JSON.parse(localStorage.getItem('recentlyExitedTables') || '{}');
        if (stored && typeof stored === 'object' && String(gameID) in stored) {
          delete stored[String(gameID)];
          localStorage.setItem('recentlyExitedTables', JSON.stringify(stored));
        }
      } catch {
        /* noop */
      }
      // …and the player's own exit of it: opening it again brings it back.
      clearPlayerExitedTable(gameID);

      // How many tables will be open once this call settles. THE GRID IS ONLY
      // WORTH ENTERING AT TWO. With one table it has a single cell, and
      // MultiTableGrid immediately hands back to single-table view
      // (gridIsTooNarrowToBeUseful — tablesAcross is clamped by the open-table
      // count), which the player sees as a full second of grid chrome before
      // the real table appears. Jason hit this every time he opened his only
      // table from the lobby fan (2026-08-17). Going straight to single-table
      // is both faster and the truthful answer: one table is not a grid.
      const openAfterThisCall = slotUsage.isAlreadyOpen(gameID)
        ? slotUsage.openIds.size
        : slotUsage.openIds.size + 1;
      // ...AND the window must actually fit two. Without the width test, a
      // 500px window ran the full round trip on EVERY swipe: openTable →
      // /grid → MultiTableGrid sees gridIsTooNarrowToBeUseful → hands straight
      // back to /table. The player saw the All Tables page flash past on each
      // table change (Jason 2026-08-18). Same width predicate the grid itself
      // uses, so the two can never disagree about whether a grid is worth
      // having here.
      const gridFitsHere = tablesThatFit(window.innerWidth) >= 2;
      const gridWorthEntering =
        multiTableEnabled &&
        !isMobile &&
        !opts?.forceSingle &&
        gridFitsHere &&
        openAfterThisCall >= 2;

      // The chosen layout is honoured across lobby round-trips: an already-open
      // table is focused in the grid rather than dropping to single-table view.
      // `forceSingle` lets the explicit "one table" choice bypass this
      // regardless of the (about-to-change) preference.
      if (gridWorthEntering && slotUsage.isAlreadyOpen(gameID)) {
        // Supersede any in-flight direct-grid open (see pendingGridReturnRef):
        // the player explicitly switched tables, so an earlier open that never
        // landed must not keep holding the narrow handoff — or steal the view
        // back if its table lands later (dev review #3760).
        pendingGridReturnRef.current = null;
        setSelectedGridGameID(String(gameID));
        navigate('/grid', { replace: true });
        startTransition();
        return true;
      }

      // Not open yet, and other tables ARE already open: subscribe it directly
      // and go STRAIGHT to the grid — no detour through single-table view. It
      // joins as a cell the moment its first broadcast lands; the effect below
      // focuses it then.
      if (gridWorthEntering && slotUsage.openIds.size > 0) {
        subscribeToNewTable(String(gameID), gameType);
        // Re-armed FRESH — a newer open supersedes whatever was pending.
        pendingGridReturnRef.current = { gameID: String(gameID), armedAtMs: Date.now() };
        navigate('/grid');
        startTransition();
        return true;
      }

      // Single-table routing: multi-table off, a forced-single open, or this
      // would be the player's ONLY table. No pending grid return is armed in
      // that last case — sending a lone table to the grid just to be handed
      // straight back is the flash this branch exists to avoid.
      if (gridWorthEntering) {
        pendingGridReturnRef.current = { gameID: String(gameID), armedAtMs: Date.now() };
      } else {
        // Supersede, same as the grid-focus branch above. The grid's own
        // narrow handoff re-enters HERE (it opens with forceSingle), so
        // clearing is what lets an expired hold hand off cleanly: the
        // handoff decided the view, and a stale pending must not re-steal
        // it (dev review #3760).
        pendingGridReturnRef.current = null;
      }

      const observeParam = observe === 'yes' ? '?observe=yes' : '';
      navigate(`/table/${gameID}/${gameType}${observeParam}`, { replace: true });
      startTransition();
      return true;
    },
    [
      navigate,
      startTransition,
      readSlotUsage,
      readHeldSlots,
      multiTableEnabled,
      isMobile,
      isMaintenance,
      subscribeToNewTable
    ]
  );

  // Focus (and, if needed, navigate to) the grid once a table opened for
  // multi-table view has subscribed (landed in state.tables). Two paths set
  // Keep the refresh-restore effect's escape hatch pointed at the live
  // callbacks — it is armed on mount, before these exist.
  openTableRef.current = openTable;
  subscribeToNewTableRef.current = subscribeToNewTable;

  // Read the pending open, retiring it on the spot once it has outlived its
  // ceiling — no reader ever acts on an expired hold. A ref check, not state:
  // expiry only matters at the moment something consults the pending, so the
  // consumers (MultiTableGrid's handoff hold, the focus effect below) check as
  // they read. Waking up to DO that read after the ceiling is the consumer's
  // job — see MultiTableGrid's holdForPendingGridOpen (dev review #3760).
  const readPendingGridOpen = useCallback((): { gameID: string; armedAtMs: number } | null => {
    const pending = pendingGridReturnRef.current;
    if (!pending) return null;
    if (Date.now() - pending.armedAtMs > PENDING_GRID_OPEN_MAX_MS) {
      pendingGridReturnRef.current = null;
      return null;
    }
    return pending;
  }, []);

  // Read by MultiTableGrid at effect time — see the context type's docblock.
  const hasPendingGridOpen = useCallback(
    () => readPendingGridOpen() !== null,
    [readPendingGridOpen]
  );

  // pendingGridReturnRef:
  //   • direct-grid open (other tables already open) — already on /grid, so this
  //     just focuses the new cell when it appears.
  //   • first-table open — routed via /table to subscribe; once it lands we
  //     switch to /grid with it focused (an empty grid would have bounced to the
  //     lobby, so we couldn't navigate before it existed).
  useEffect(() => {
    // Expiry-checked read: a pending past its ceiling is dead HERE too. The
    // stuck open's table can still land much later (a reconnect finally
    // delivering it), and focusing it — or navigating to the grid for it —
    // then would be the same view-steal the ceiling exists to end, from the
    // other side (dev review #3760).
    const pending = readPendingGridOpen();
    if (!pending) return;
    // Player turned multi-table off (or we're on mobile) → abandon.
    if (!multiTableEnabled || isMobile) {
      pendingGridReturnRef.current = null;
      return;
    }
    // If on a table URL, only act for the pending table's own view — never yank
    // the player off a different table, and avoid the race before navigate has
    // moved the URL. (On /grid or /lobby this guard is skipped.)
    if (activeView === 'table' && activeTableInfo?.gameID !== pending.gameID) return;
    const subscribed =
      Boolean(tables?.[pending.gameID]?.game?.gameID) && tables[pending.gameID].game.gameID !== 0;
    if (!subscribed) return;
    // Same rule as openTable: never navigate INTO a one-cell grid. Between
    // arming this and the table landing, another table can have been closed —
    // and the grid would take the player only to hand them straight back. The
    // grid's own count decides, so a loaded table it hides can't (EN-11092).
    const loadedCount = Object.values(tables || {}).filter(
      (t: any) => t?.game?.gameID && t.game.gameID !== 0
    ).length;
    const openCount = gridTableCount ?? loadedCount;
    // The grid reports its count from its own effect, which runs in this same
    // commit, so the table that just landed isn't in `gridTableCount` yet. Wait
    // for that report (it re-runs this effect) instead of consuming the pending
    // on the stale count — doing so left the new table unfocused (review
    // #3821). Only while the loaded tables say there IS a second table: a
    // landing that really leaves one is settled now, as before, rather than
    // holding the grid's one-table handoff until the pending's ceiling.
    if (openCount < 2 && loadedCount >= 2) return;
    pendingGridReturnRef.current = null;
    if (openCount < 2) return;
    setSelectedGridGameID(pending.gameID);
    if (activeView !== 'grid') {
      navigate('/grid');
      startTransition();
    }
  }, [
    tables,
    gridTableCount,
    activeView,
    activeTableInfo,
    multiTableEnabled,
    isMobile,
    navigate,
    startTransition,
    readPendingGridOpen
  ]);

  const returnToLobby = useCallback(() => {
    // The player chose to leave, even if the debounce drops the click.
    returnToGridRef.current = false;
    autoViewSwitchesRef.current = [];
    if (transitionRef.current) return;
    console.log('[ViewManager] returnToLobby');
    navigate('/', { replace: true });
    startTransition();
  }, [navigate, startTransition]);

  /**
   * A NEW table just arrived → show as many felts as this window can hold.
   *
   * Adding a table is the moment the player's answer to "how many do I want?"
   * has changed, so the layout follows without being asked (Jason 2026-08-18:
   * two tables at 1400px, add a third → three-up on the grid). The ceiling
   * comes from clampTablesInView with no stored preference, so it is exactly
   * min(what fits, what is open, the four-table cap).
   *
   * Watches the COUNT rather than hooking openTable, so tables that arrive
   * without a click — an SnG auto-opening, a tournament seat — get the same
   * treatment. Narrow windows opt out for free: the ceiling is 1 there, and a
   * one-table ceiling never turns the grid on.
   *
   * Only a count past the most tables open since the count was last set is a
   * new table. Anything at or below it is a table coming back: one of a
   * reload's restored set (tables land one broadcast at a time, and
   * `state.tables` is a fresh object on every broadcast, so neither "the first
   * arrival" nor "the first tick with no new table" marks the end of a
   * restore — Marlon QA 8/24, JRam #3721), or one that dropped out and
   * returned — a rebalance, a re-entry, a reconnect. Growing on those replaced
   * the player's count: 2-up with three open, a rebalance dips to two and back,
   * and the view came back three-up.
   *
   * And a new table grows the view only when the player was seeing all the
   * tables they had: someone watching two of three, or one table in single
   * view, chose that, and keeps it. A chosen count of one never grows (it is
   * only ever set from the header, which also turns multi-table view off).
   * Growing never lowers a larger count.
   */
  useEffect(() => {
    const count = Object.values(tables || {}).filter(
      (t: any) => t?.game?.gameID && t.game.gameID !== 0
    ).length;
    const previous = openTableCountRef.current;
    openTableCountRef.current = count;
    const restore = restoreTargetRef.current;
    if (count >= restore.count || Date.now() >= restore.until) restore.count = 0;
    const mostOpen = Math.max(mostOpenSinceChoiceRef.current, restore.count);
    mostOpenSinceChoiceRef.current = Math.max(mostOpenSinceChoiceRef.current, count);
    if (previous === null || isMobile) return;
    if (count <= mostOpen) return;
    const chosen = tablesInViewRef.current;
    // One table is only ever the player's own choice (growing sets two or
    // more), and it turned multi-table view off.
    if (chosen === 1) return;
    if (chosen !== null && chosen < mostOpen) return;
    const ceiling = clampTablesInView(null, window.innerWidth, count);
    if (ceiling < 2) return;
    if (chosen !== null && chosen >= ceiling) return;
    setTablesInView(ceiling);
    setMultiTableEnabled(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tables, isMobile]);

  /**
   * Back to the grid once a table the count lost returns.
   *
   * When the open tables fall to one, the grid hands the player to single-table
   * view (MultiTableGrid's too-narrow handoff, with forceSingle so the sticky
   * grid preference can't bounce them straight back). That is right for the
   * moment — one table is not a grid — but the drop is often a table on its
   * way back: the old table of a rebalance or re-entry removed before the new
   * one loads, or a reconnect. The player's count was never changed, yet
   * single view shows one table, and nothing brought them back when the second
   * table landed.
   *
   * The grid says when it makes that handoff (handOffGridToTable), and when the
   * window fits two the return is armed. The grid comes back once two tables
   * are loaded — on the table the player is looking at — even if the second
   * was back before the handoff's route landed. Any navigation the player makes
   * in the meantime cancels it (openTable, the lobby, the grid — even one the
   * debounce drops), as does
   * choosing one table (which turns multi-table view off). A return held by the
   * handoff's transition debounce waits it out on a timer, since no broadcast
   * may come to re-run this.
   */
  const handOffGridToTable = useCallback(
    (gameID: string, gameType: string): boolean => {
      const accepted = openTable(gameID, gameType, 'no', { forceSingle: true });
      if (accepted) {
        returnToGridRef.current = tablesThatFit(window.innerWidth) >= 2;
        autoViewSwitchesRef.current = withAutoViewSwitch(autoViewSwitchesRef.current, Date.now());
      }
      return accepted;
    },
    [openTable]
  );
  // What consumers get as openTable: the player navigating, which cancels a
  // pending return to the grid — even when a guard (the debounce) drops it.
  const openTableCancellingReturn = useCallback<ViewManagerContextType['openTable']>(
    (gameID, gameType, observe, opts) => {
      returnToGridRef.current = false;
      autoViewSwitchesRef.current = [];
      return openTable(gameID, gameType, observe, opts);
    },
    [openTable]
  );
  const [gridReturnRecheck, setGridReturnRecheck] = useState(0);
  useEffect(() => {
    if (!returnToGridRef.current) return undefined;
    // The handoff's route hasn't landed yet.
    if (activeView === 'grid') return undefined;
    const stillWanted =
      activeView === 'table' &&
      !isMobile &&
      multiTableEnabled &&
      (tablesInView === null || tablesInView >= 2);
    if (!stillWanted) {
      returnToGridRef.current = false;
      return undefined;
    }
    // The grid's own count, the one its handoff decided on. Counting raw
    // state.tables here disagreed with it whenever a loaded table is one the
    // grid doesn't show (recently exited, filtered, a phone session's table):
    // every handoff re-armed a return the next 1/s broadcast took, and the view
    // flipped /table ↔ /grid until the counts happened to agree (EN-11092).
    const rawCount = openTableCountRef.current ?? 0;
    const count = gridTableCount ?? rawCount;
    if (count < 2 || tablesThatFit(window.innerWidth) < 2) return undefined;
    if (transitionRef.current) {
      const id = setTimeout(() => setGridReturnRecheck((n) => n + 1), TABLE_TRANSITION_MS + 50);
      return () => clearTimeout(id);
    }
    returnToGridRef.current = false;
    // Loop guard: whatever else makes the two sides disagree, the client never
    // keeps switching views by itself. Past one round trip in the window this
    // return is refused and the player stays on the table, where they can act
    // and exit (see AUTO_VIEW_SWITCH_WINDOW_MS for why not a strict cap of two).
    const nowMs = Date.now();
    if (autoViewSwitchesExhausted(autoViewSwitchesRef.current, nowMs)) {
      console.warn('[ViewManager] Stopped a /table ↔ /grid loop; staying on the table', {
        gridTableCount,
        loadedTables: rawCount
      });
      return undefined;
    }
    autoViewSwitchesRef.current = withAutoViewSwitch(autoViewSwitchesRef.current, nowMs);
    if (activeTableInfo?.gameID) setSelectedGridGameID(String(activeTableInfo.gameID));
    navigate('/grid', { replace: true });
    startTransition();
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    tables,
    gridTableCount,
    activeView,
    multiTableEnabled,
    tablesInView,
    isMobile,
    gridReturnRecheck
  ]);

  const openGrid = useCallback(() => {
    returnToGridRef.current = false;
    autoViewSwitchesRef.current = [];
    if (transitionRef.current) return;
    // Entering the grid IS the player opting into multi-table view — persist it
    // so subsequent table opens stay in the grid until they switch back.
    setMultiTableEnabled(true);
    navigate('/grid', { replace: true });
    startTransition();
  }, [navigate, startTransition, setMultiTableEnabled]);

  // Active-player tournament restriction is enforced per-table in GameInner
  // (pages/Game.tsx). Each GameInner instance — whether mounted by the
  // /table/:id route or by MultiTableGrid for /grid — has its own
  // TableIdContext bound to its specific gameID, so it can call the real
  // GameService.leaveTable with the correct baseParams (engine SITOUT +
  // localStorage flag + unsubscribe), then remove its own slice and (in
  // single-table mode) redirect to the player's assigned seat.

  // PERF: memoized so consumers only re-render when one of these values
  // actually changes, not on every provider re-render. See
  // docs/investigations/20260819-game-client-overheating.md.
  const value = useMemo(
    () => ({
      activeView,
      activeTableInfo,
      isTableMounted,
      tableKey,
      gridPage,
      openTable: openTableCancellingReturn,
      handOffGridToTable,
      reportGridTableCount,
      returnToLobby,
      openGrid,
      setGridPage,
      multiTableEnabled,
      tablesInView,
      restoreInFlight,
      setTablesInView,
      tableWindowStart,
      setTableWindowStart,
      setMultiTableEnabled,
      selectedGridGameID,
      selectGridTable,
      hasPendingGridOpen,
      gridRebind,
      rebindGridTable,
      clearGridRebind,
      requestTableSlot,
      wasPostLoginLanding
    }),
    [
      activeView,
      activeTableInfo,
      isTableMounted,
      tableKey,
      gridPage,
      openTableCancellingReturn,
      handOffGridToTable,
      reportGridTableCount,
      returnToLobby,
      openGrid,
      multiTableEnabled,
      tablesInView,
      restoreInFlight,
      tableWindowStart,
      setMultiTableEnabled,
      selectedGridGameID,
      selectGridTable,
      hasPendingGridOpen,
      gridRebind,
      rebindGridTable,
      clearGridRebind,
      requestTableSlot,
      wasPostLoginLanding
    ]
  );

  return (
    <ViewManagerContext.Provider value={value}>
      {children}
      {/* Waitlist is enabled in this client (lobby cash tiles set
          waitlistEnabled), so the waitlist-aware body variant is correct. */}
      <MaximumTablesReachedAlert
        open={showTableLimitModal}
        waitlistEnabled
        onClose={() => setShowTableLimitModal(false)}
      />
    </ViewManagerContext.Provider>
  );
};
